import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Barbell,
  CalendarCheck,
  ChartDonut,
  ForkKnife,
  Info,
  Ruler,
  Sparkle,
  Target,
  TrendUp,
} from "@phosphor-icons/react";
import TimePicker from "../../ui/TimePicker";
import GoalSuggestion from "./GoalSuggestion";
import { selectClinicId } from "../../../store/slices/authSlice";
import { useGetMeasurementsQuery } from "../../../store/services/measurementsApi";
import {
  ACTIVITY_LEVEL_LABELS,
  DEFAULT_GOAL_TYPE,
  DIET_PLANS,
  GOAL_TYPES,
  calcAgeFromBirthDate,
  calcBmr,
  calcTdee,
  macrosFromDietPlan,
  suggestDailyGoals,
} from "../../../utils/nutritionCalc";
import "../PatientToggles.css";
import "./NutritionSettingsSection.css";

/**
 * "תזונה" — the per-client nutrition settings, shared by the create wizard and
 * the patient-profile edit mode (the same pattern as EnglishCommunicationToggle).
 *
 * Writes the optional `patient.nutrition` map. Everything here is absent on a
 * patient that was never edited in a nutrition clinic, so every read defaults.
 *
 * Purely presentational: the parent owns the value and the change handler.
 */

const MACRO_FIELDS = [
  { key: "calories", label: "קלוריות", unit: "קק״ל" },
  { key: "protein", label: "חלבון", unit: "ג׳" },
  { key: "carbohydrates", label: "פחמימות", unit: "ג׳" },
  { key: "fat", label: "שומן", unit: "ג׳" },
];

const FEATURE_TOGGLES = [
  {
    key: "selfLoggingEnabled",
    icon: ForkKnife,
    title: "דיווח אכילה עצמאי",
    sub: "הלקוח/ה יוכל/תוכל לדווח מה אכל/ה בעמוד האישי",
  },
  {
    key: "aiEstimateEnabled",
    icon: Sparkle,
    title: "פירוק ארוחה אוטומטי (AI)",
    sub: "המערכת תפרק את הטקסט לערכים תזונתיים",
  },
  {
    key: "selfMeasurementsEnabled",
    icon: Ruler,
    title: "מדידות עצמאיות",
    sub: "הלקוח/ה יוכל/תוכל להוסיף מדידות בעצמו/ה",
  },
  {
    key: "showGoalsToClient",
    icon: Target,
    title: "הצגת יעדים ללקוח",
    sub: "הסיכום היומי יוצג מול היעד שהוגדר",
  },
];

const FREQUENCIES = [
  { key: "weekly", label: "שבועי" },
  { key: "biweekly", label: "דו-שבועי" },
  { key: "monthly", label: "חודשי" },
];

/** Israel week — Sunday first. */
const WEEKDAYS = [
  { value: 0, label: "ראשון" },
  { value: 1, label: "שני" },
  { value: 2, label: "שלישי" },
  { value: 3, label: "רביעי" },
  { value: 4, label: "חמישי" },
  { value: 5, label: "שישי" },
  { value: 6, label: "שבת" },
];

/** `showGoalsToClient` defaults ON; the other switches default OFF. */
const isToggleOn = (nutrition, key) =>
  key === "showGoalsToClient"
    ? nutrition?.[key] !== false
    : nutrition?.[key] === true;

function NutritionSettingsSection({
  nutrition,
  onChange,
  patient,
  latestMeasurement = null,
}) {
  const settings = nutrition || {};
  const goals = settings.goals || {};
  const schedule = settings.measurementSchedule || {};

  // The parent never passed a measurement in practice; read the latest one
  // here so the guidance and the suggestions actually have weight + BMR.
  const clinicId = useSelector(selectClinicId);
  const { data: measurements = [] } = useGetMeasurementsQuery(
    { clinicId, patientId: patient?.id },
    { skip: !clinicId || !patient?.id }
  );
  const effectiveLatest = latestMeasurement || measurements[0] || null;

  /** Replace one top-level key of the nutrition map. */
  const setField = (key, value) => onChange({ ...settings, [key]: value });

  const setGoal = (key, value) =>
    onChange({ ...settings, goals: { ...goals, [key]: value } });

  const setSchedule = (key, value) =>
    onChange({
      ...settings,
      measurementSchedule: { ...schedule, [key]: value },
    });

  // BMR/TDEE are shown as guidance for picking a calorie goal — never written.
  // They need gender + age off the patient and weight + height off the most
  // recent measurement, any of which may not exist yet; then we simply show
  // nothing rather than a number built on a guessed input.
  const guidance = useMemo(() => {
    const age = calcAgeFromBirthDate(patient?.birthDate);
    const gender = patient?.gender;
    const weight = Number(effectiveLatest?.weight) || null;
    const height = Number(effectiveLatest?.height) || null;
    // The stored BMR honors a manual override; the formula is the fallback.
    const bmr = effectiveLatest?.bmr ?? calcBmr({ weight, height, age, gender });
    if (bmr === null || bmr === undefined) return null;
    return { bmr, tdee: calcTdee(bmr, settings.activityLevel), weight };
  }, [patient, effectiveLatest, settings.activityLevel]);

  // Suggested goals — helper text under each input, never auto-filled.
  const suggestions = useMemo(
    () =>
      suggestDailyGoals({
        bmr: guidance?.bmr,
        activityLevel: settings.activityLevel,
        weight: guidance?.weight ?? (Number(effectiveLatest?.weight) || null),
        goalType: settings.goalType || DEFAULT_GOAL_TYPE,
        dietPlan: goals.dietPlan,
        caloriesEntered: goals.calories,
      }),
    [guidance, effectiveLatest, settings.activityLevel, settings.goalType, goals.dietPlan, goals.calories]
  );

  /** Fill the macro goals from the calorie goal + the chosen diet plan. */
  const applyDietPlan = (planKey) => {
    const macros = macrosFromDietPlan(Number(goals.calories) || 0, planKey);
    onChange({
      ...settings,
      goals: {
        ...goals,
        dietPlan: planKey,
        // A plan of "none" (or no calorie goal yet) clears nothing — the coach
        // keeps whatever they typed by hand.
        ...(macros ? macros : {}),
      },
    });
  };

  return (
    <div className="nst-section">
      <div className="nst-header">
        <span className="nst-header-icon">
          <ForkKnife size={18} weight="fill" />
        </span>
        <div className="nst-header-text">
          <h4>תזונה</h4>
          <p>מה הלקוח/ה רואה ויכול/ה לעשות בעמוד האישי, ומה היעדים שלו/ה</p>
        </div>
      </div>

      {/* --- feature switches --- */}
      <div className="ptg-grid">
        {FEATURE_TOGGLES.map(({ key, icon: Icon, title, sub }) => {
          const isOn = isToggleOn(settings, key);
          return (
            <label key={key} className={`ptg-card ${isOn ? "is-on" : ""}`}>
              <span className="ptg-icon">
                <Icon size={17} weight={isOn ? "fill" : "duotone"} />
              </span>
              <span className="ptg-text">
                <span className="ptg-title">{title}</span>
                <span className="ptg-sub">{sub}</span>
              </span>
              <span className="ptg-switch">
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={(e) => setField(key, e.target.checked)}
                />
                <span className="ptg-slider"></span>
              </span>
            </label>
          );
        })}
      </div>

      {/* --- goal type + activity level --- */}
      <div className="nst-goals-grid">
        <div className="form-group">
          <label>
            <TrendUp size={15} weight="bold" /> מטרת הליווי
          </label>
          <select
            value={settings.goalType || DEFAULT_GOAL_TYPE}
            onChange={(e) => setField("goalType", e.target.value)}
          >
            {Object.entries(GOAL_TYPES).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>
            <Barbell size={15} weight="bold" /> רמת פעילות
          </label>
          <select
            value={settings.activityLevel || ""}
            onChange={(e) => setField("activityLevel", e.target.value || null)}
          >
            <option value="">לא הוגדר</option>
            {Object.entries(ACTIVITY_LEVEL_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* --- goals --- */}
      <div className="nst-subhead">
        <ChartDonut size={15} weight="bold" /> יעדים יומיים
      </div>

      <div className="form-group">
        <label>תוכנית תזונה</label>
        <select
          value={goals.dietPlan || ""}
          onChange={(e) => applyDietPlan(e.target.value)}
        >
          <option value="">לא הוגדר</option>
          {DIET_PLANS.map((plan) => (
            <option key={plan.key} value={plan.key}>
              {plan.label}
            </option>
          ))}
        </select>
        <span className="nst-hint">
          <Info size={13} weight="bold" />
          בחירת תוכנית ממלאת את החלבון, הפחמימות והשומן לפי יעד הקלוריות
        </span>
      </div>

      <div className="nst-goals-grid">
        {MACRO_FIELDS.map(({ key, label, unit }) => (
          <div className="form-group" key={key}>
            <label>
              {label} <span className="nst-unit">({unit})</span>
            </label>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              dir="ltr"
              lang="en"
              value={goals[key] ?? ""}
              onChange={(e) =>
                setGoal(key, e.target.value === "" ? null : Number(e.target.value))
              }
              placeholder="—"
            />
            <GoalSuggestion suggestion={suggestions?.[key]} />
          </div>
        ))}
      </div>

      {guidance && (
        <div className="nst-guidance">
          <Info size={14} weight="bold" />
          <span>
            להערכה בלבד: קצב חילוף חומרים במנוחה כ-{guidance.bmr} קק״ל
            {guidance.tdee ? `, והוצאה יומית משוערת כ-${guidance.tdee} קק״ל` : ""}
          </span>
        </div>
      )}

      {/* --- measurement schedule --- */}
      <div className="nst-subhead">
        <CalendarCheck size={15} weight="bold" /> תזכורת מדידות
      </div>

      <label className={`ptg-card ${schedule.enabled === true ? "is-on" : ""}`}>
        <span className="ptg-icon">
          <Ruler size={17} weight={schedule.enabled === true ? "fill" : "duotone"} />
        </span>
        <span className="ptg-text">
          <span className="ptg-title">מעקב מדידות קבוע</span>
          <span className="ptg-sub">
            כשמגיע המועד תופיע תזכורת ברשימת הפעולות הממתינות
          </span>
        </span>
        <span className="ptg-switch">
          <input
            type="checkbox"
            checked={schedule.enabled === true}
            onChange={(e) => setSchedule("enabled", e.target.checked)}
          />
          <span className="ptg-slider"></span>
        </span>
      </label>

      {schedule.enabled === true && (
        <div className="nst-schedule-grid">
          <div className="form-group">
            <label>תדירות</label>
            <select
              value={schedule.frequency || "weekly"}
              onChange={(e) => setSchedule("frequency", e.target.value)}
            >
              {FREQUENCIES.map((frequency) => (
                <option key={frequency.key} value={frequency.key}>
                  {frequency.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>יום בשבוע</label>
            <select
              value={schedule.weekday ?? 0}
              onChange={(e) => setSchedule("weekday", Number(e.target.value))}
            >
              {WEEKDAYS.map((day) => (
                <option key={day.value} value={day.value}>
                  יום {day.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>שעה</label>
            <TimePicker
              value={schedule.time || "08:00"}
              onChange={(e) =>
                setSchedule("time", e?.target ? e.target.value : e)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default NutritionSettingsSection;
